import { useState, useRef, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import apiClient from '@/services/api/client';
import { ChartRenderer, parseContentWithCharts } from '@/components/chat/ChatChartRenderer';
import { chatSessionsApi, ChatSession, ChatMessage as ApiChatMessage } from '@/services/api/chat-sessions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Send,
  Loader2,
  BookOpen,
  FileText,
  AlertCircle,
  Bot,
  User,
  Trash2,
  ThumbsUp,
  ThumbsDown,
  MessageSquareWarning,
  FileQuestion,
  ChevronDown,
  Copy,
  Check,
  Plus,
  MessageSquare,
  Clock,
  Pencil,
  History,
  PanelLeftClose,
  PanelLeft,
  ExternalLink,
  X,
  FolderOpen,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';

interface Citation {
  id: number;
  documentId?: string;
  filename: string;
  excerpt: string;
  fullContent?: string;
  similarity?: number;
  source?: 'tenant' | 'global' | 'client_file';
  clientName?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  timestamp: Date;
  error?: boolean;
  queryId?: string;
  feedbackGiven?: 'helpful' | 'not_helpful' | 'incorrect' | 'incomplete';
  originalQuestion?: string;
  isExplanation?: boolean;
  canExplainMore?: boolean;
}

interface ChatResponse {
  message: string;
  sessionId?: string;
  queryId?: string;
  mode: string;
  citations?: Citation[];
  responseTime?: number;
  modelId?: string;
  error?: boolean;
  isExplanation?: boolean;
  kbStatus?: {
    isReady: boolean;
    documentCount: number;
    status: string;
  };
}

type FeedbackRating = 'helpful' | 'not_helpful' | 'incorrect' | 'incomplete';

function truncateMiddle(str: string, maxLen = 48): string {
  if (str.length <= maxLen) return str;
  const half = Math.floor((maxLen - 3) / 2);
  return str.slice(0, half) + '...' + str.slice(-half);
}

// Inline Citation Component - small superscript number
function InlineCitation({
  citations,
  onCitationClick
}: {
  citations: Citation[];
  onCitationClick: (citation: Citation) => void;
}) {
  if (!citations || citations.length === 0) return null;

  // Get unique citations by id to avoid duplicates
  const uniqueCitations = citations.filter(
    (c, i, arr) => arr.findIndex(x => x.id === c.id) === i
  );

  return (
    <TooltipProvider>
      <span className="inline-flex items-baseline gap-0.5 ml-0.5">
        {uniqueCitations.slice(0, 3).map((citation, idx) => (
          <Tooltip key={citation.id} cursor={false}>
            <TooltipTrigger asChild>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCitationClick(citation);
                }}
                className="inline-flex items-center justify-center min-w-[14px] h-[14px] text-[9px] font-semibold bg-primary/15 hover:bg-primary/25 text-primary rounded-sm cursor-pointer transition-colors align-super -mt-1"
              >
                {citation.id}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs font-medium">{citation.filename}</p>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                {citation.excerpt.substring(0, 100)}...
              </p>
            </TooltipContent>
          </Tooltip>
        ))}
        {uniqueCitations.length > 3 && (
          <span className="text-[9px] text-muted-foreground align-super -mt-1">
            +{uniqueCitations.length - 3}
          </span>
        )}
      </span>
    </TooltipProvider>
  );
}

// Common stopwords to ignore when matching
const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with',
  'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been', 'be', 'have', 'has', 'had',
  'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must',
  'shall', 'can', 'need', 'dare', 'ought', 'used', 'it', 'its', 'this', 'that',
  'these', 'those', 'i', 'you', 'he', 'she', 'we', 'they', 'what', 'which', 'who',
  'whom', 'when', 'where', 'why', 'how', 'all', 'each', 'every', 'both', 'few',
  'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own',
  'same', 'so', 'than', 'too', 'very', 'just', 'also', 'now', 'here', 'there',
  'then', 'once', 'if', 'because', 'about', 'into', 'through', 'during', 'before',
  'after', 'above', 'below', 'between', 'under', 'again', 'further', 'while',
  'being', 'having', 'doing', 'their', 'your', 'our', 'his', 'her', 'any', 'many'
]);

// Function to extract key terms from text
function extractKeyTerms(text: string): Set<string> {
  const words = text
    .toLowerCase()
    .replace(/[*_`#\[\](){}:;,.!?\-"']/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 3 && !STOPWORDS.has(w));
  return new Set(words);
}

// Function to find sentences in source that are relevant to the answer
function findMatchingPhrases(answerText: string, sourceText: string): string[] {
  if (!answerText || !sourceText) return [];

  // Extract key terms from the answer
  const answerTerms = extractKeyTerms(answerText);
  if (answerTerms.size === 0) return [];

  // Split source into sentences (handle various sentence endings and list items)
  const sourceSentences = sourceText
    .split(/(?<=[.!?])\s+|(?=[-•*]\s)|(?=\d+\.\s)|\n+/)
    .map(s => s.trim())
    .filter(s => s.length > 15); // Only sentences with meaningful content

  // Score each sentence by how many answer terms it contains
  const scoredSentences: { sentence: string; score: number; matchedTerms: string[] }[] = [];

  for (const sentence of sourceSentences) {
    const sentenceTerms = extractKeyTerms(sentence);
    const matchedTerms: string[] = [];

    for (const term of answerTerms) {
      if (sentenceTerms.has(term)) {
        matchedTerms.push(term);
      }
    }

    // Require at least 2 matching terms for relevance
    if (matchedTerms.length >= 2) {
      // Score based on number of matches and density (matches per sentence length)
      const score = matchedTerms.length + (matchedTerms.length / sentenceTerms.size) * 2;
      scoredSentences.push({ sentence, score, matchedTerms });
    }
  }

  // Sort by score descending and take top matches
  scoredSentences.sort((a, b) => b.score - a.score);

  // Return unique sentences, avoiding overlaps
  const results: string[] = [];
  const usedText = new Set<string>();

  for (const { sentence } of scoredSentences) {
    const normalized = sentence.toLowerCase();
    // Skip if this sentence overlaps significantly with already added ones
    let isOverlap = false;
    for (const used of usedText) {
      if (normalized.includes(used) || used.includes(normalized)) {
        isOverlap = true;
        break;
      }
    }

    if (!isOverlap) {
      results.push(sentence);
      usedText.add(normalized);
      if (results.length >= 8) break; // Limit to 8 highlighted sentences
    }
  }

  return results;
}

// Component to render text with highlighted phrases
function HighlightedText({ text, highlights }: { text: string; highlights: string[] }) {
  if (!highlights || highlights.length === 0) {
    return <>{text}</>;
  }

  // Create a regex pattern to match all highlights
  const escapedHighlights = highlights.map(h =>
    h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );
  const pattern = new RegExp(`(${escapedHighlights.join('|')})`, 'gi');

  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, index) => {
        const isHighlight = highlights.some(h =>
          h.toLowerCase() === part.toLowerCase()
        );

        if (isHighlight) {
          return (
            <mark
              key={index}
              className="bg-yellow-200 dark:bg-yellow-900/50 text-inherit px-0.5 rounded-sm"
            >
              {part}
            </mark>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

// Citation Detail Dialog - shown when clicking on inline citation
function CitationDetailDialog({
  citation,
  open,
  onOpenChange,
  answerText,
}: {
  citation: Citation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  answerText?: string;
}) {

  // Compute fullText safely (handle null citation)
  const fullText = citation?.fullContent || citation?.excerpt || '';

  // Find matching phrases to highlight - hooks must be called unconditionally
  const highlights = useMemo(() => {
    if (!answerText || !citation) return [];
    return findMatchingPhrases(answerText, fullText);
  }, [answerText, fullText, citation]);

  // Custom markdown components to apply highlighting - must be called unconditionally
  const markdownComponents: Components = useMemo(() => ({
    p: ({ children }) => {
      const text = extractTextFromChildren(children);
      return (
        <p className="my-2">
          <HighlightedText text={text} highlights={highlights} />
        </p>
      );
    },
    li: ({ children }) => {
      const text = extractTextFromChildren(children);
      return (
        <li className="my-0.5">
          <HighlightedText text={text} highlights={highlights} />
        </li>
      );
    },
    strong: ({ children }) => {
      const text = extractTextFromChildren(children);
      return (
        <strong>
          <HighlightedText text={text} highlights={highlights} />
        </strong>
      );
    },
    table: ({ children, ...props }) => (
      <div className="my-2 overflow-hidden w-full">
        <table {...props} className="text-xs w-full table-fixed border-collapse border border-zinc-300 dark:border-zinc-600 [&_td]:border [&_td]:border-zinc-300 dark:border-zinc-600 [&_td]:p-1.5 [&_td]:whitespace-normal [&_td]:break-words [&_td]:overflow-hidden [&_td]:align-top [&_th]:border [&_th]:border-zinc-300 dark:border-zinc-600 [&_th]:p-1.5 [&_th]:whitespace-normal [&_th]:break-words [&_th]:overflow-hidden [&_th]:bg-zinc-100 dark:[&_th]:bg-zinc-800 [&_th]:font-semibold">{children}</table>
      </div>
    ),
    pre: ({ children, ...props }) => (
      <pre {...props} className="my-2 bg-muted text-foreground overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full" style={{ scrollbarWidth: 'thin' }}>
        {children}
      </pre>
    ),
  }), [highlights]);

  // Early return AFTER all hooks
  if (!citation) return null;

  const getFileTypeInfo = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return { label: 'PDF', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: '📄' };
    if (['doc', 'docx'].includes(ext || '')) return { label: 'Word', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: '📝' };
    if (['xls', 'xlsx'].includes(ext || '')) return { label: 'Excel', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: '📊' };
    if (['txt', 'md'].includes(ext || '')) return { label: 'Text', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: '📃' };
    if (ext === 'html') return { label: 'HTML', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: '🌐' };
    return { label: ext?.toUpperCase() || 'File', color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400', icon: '📎' };
  };

  const fileType = getFileTypeInfo(citation.filename);
  const relevancePercent = citation.similarity ? Math.round(citation.similarity * 100) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-3 border-b bg-muted/30">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-lg">
                {fileType.icon}
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-base font-semibold pr-8" title={citation.filename}>
                  {truncateMiddle(citation.filename)}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant="secondary" className={cn("text-[10px] px-1.5 py-0", fileType.color)}>
                    {fileType.label}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Source #{citation.id}
                  </span>
                  {relevancePercent && (
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] px-1.5 py-0",
                        relevancePercent >= 70 ? "border-green-500/50 text-green-600 dark:text-green-400" :
                        relevancePercent >= 50 ? "border-yellow-500/50 text-yellow-600 dark:text-yellow-400" :
                        "border-orange-500/50 text-orange-600 dark:text-orange-400"
                      )}
                    >
                      {relevancePercent}% match
                    </Badge>
                  )}
                  {citation.source === 'client_file' && citation.clientName && (
                    <Badge className="text-[10px] px-1.5 py-0 bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                      Client: {citation.clientName}
                    </Badge>
                  )}
                  {citation.source !== 'client_file' && (
                    <Badge className="text-[10px] px-1.5 py-0 bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30">
                      Knowledge Base
                    </Badge>
                  )}
                  {highlights.length > 0 && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-yellow-500/50 bg-yellow-100/50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400">
                      {highlights.length} highlighted
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            {citation.documentId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 shrink-0 text-xs gap-1 mr-6"
                onClick={async () => {
                  try {
                    // Pick the right download endpoint based on citation source.
                    //   global       → global KB documents table
                    //   client_file  → tenant-synced files (from cross-client search)
                    //   other        → legacy /documents path (per-client uploads)
                    const endpoint =
                      citation.source === 'global'
                        ? `/global-knowledge/documents/${citation.documentId}/download`
                        : citation.source === 'client_file'
                          ? `/synced-files/${citation.documentId}/download`
                          : `/documents/${citation.documentId}/download`;
                    const res = await apiClient.get(endpoint);
                    const url = res.data?.data?.downloadUrl || res.data?.downloadUrl || res.data?.data?.url || res.data?.url;
                    if (url) window.open(url, '_blank');
                    else toast.error('No download URL returned');
                  } catch (err: any) {
                    toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to open file');
                  }
                }}
                title="Open full file"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="hidden sm:inline">Open File</span>
              </Button>
            )}
          </div>
        </DialogHeader>

        <div
          className="overflow-y-auto overflow-x-hidden p-4 max-h-[calc(80vh-120px)] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50"
          style={{ scrollbarWidth: 'thin' }}
        >
          <div className="prose prose-sm dark:prose-invert max-w-none break-words prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-headings:my-3 prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-pre:my-2 prose-pre:bg-muted prose-pre:text-foreground prose-code:text-primary prose-code:bg-primary/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-primary/50 prose-blockquote:bg-muted/30 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:not-italic overflow-hidden">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={markdownComponents}>
              {convertTableBlocks(fullText)}
            </ReactMarkdown>
          </div>
        </div>

        {relevancePercent && (
          <div className="flex items-center gap-3 px-4 py-3 border-t bg-muted/20">
            <span className="text-xs text-muted-foreground font-medium">Relevance:</span>
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  relevancePercent >= 70 ? "bg-green-500" :
                  relevancePercent >= 50 ? "bg-yellow-500" : "bg-orange-500"
                )}
                style={{ width: `${relevancePercent}%` }}
              />
            </div>
            <span className={cn(
              "text-xs font-semibold min-w-[40px] text-right",
              relevancePercent >= 70 ? "text-green-600 dark:text-green-400" :
              relevancePercent >= 50 ? "text-yellow-600 dark:text-yellow-400" : "text-orange-600 dark:text-orange-400"
            )}>
              {relevancePercent}%
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Function to find which citations are relevant to a given text block
function findRelevantCitations(text: string, citations: Citation[]): Citation[] {
  if (!citations || citations.length === 0) return [];

  const textLower = text.toLowerCase();
  const words = textLower.split(/\s+/).filter(w => w.length > 4);

  return citations.filter(citation => {
    const excerptLower = (citation.excerpt || '').toLowerCase();
    // Check if any significant words from the text appear in the citation
    const matchCount = words.filter(word => excerptLower.includes(word)).length;
    return matchCount >= Math.min(2, words.length * 0.2); // At least 2 words or 20% match
  }).slice(0, 3); // Max 3 citations per block
}

// Get the set of citation IDs actually referenced inline in the answer
function getCitedCitationIds(content: string, citations: Citation[]): Set<number> {
  if (!citations || citations.length === 0) return new Set();
  const citedIds = new Set<number>();
  const lines = content.split('\n');
  let currentBlock = '';
  let blockIndex = 0;

  const addFromBlock = (text: string, useFallback: boolean) => {
    const relevant = findRelevantCitations(text, citations);
    if (relevant.length > 0) {
      relevant.forEach(c => citedIds.add(c.id));
    } else if (useFallback) {
      citations.slice(0, 2).forEach(c => citedIds.add(c.id));
    }
  };

  for (const line of lines) {
    const isListItem = /^[\s]*[-*•]\s/.test(line) || /^[\s]*\d+\.\s/.test(line);
    const isHeading = /^#{1,6}\s/.test(line);
    const isEmpty = line.trim() === '';

    if (isEmpty && currentBlock.trim()) {
      addFromBlock(currentBlock, blockIndex < 3);
      currentBlock = '';
      blockIndex++;
    } else if (isListItem || isHeading) {
      if (currentBlock.trim()) {
        addFromBlock(currentBlock, blockIndex < 3);
        currentBlock = '';
        blockIndex++;
      }
      addFromBlock(line, false);
      blockIndex++;
    } else {
      currentBlock += (currentBlock ? '\n' : '') + line;
    }
  }

  if (currentBlock.trim()) {
    addFromBlock(currentBlock, blockIndex < 3);
  }

  return citedIds;
}

// Component that renders markdown content with inline citations
/**
 * Renders an assistant message with inline charts.
 * The LLM emits chart JSON inside `[CHART]...[/CHART]` blocks. We split the
 * content into alternating text/chart parts, render each chart via the
 * shared ChartRenderer, and run the existing citation-aware Markdown
 * renderer on the surrounding text.
 */
function ChatMessageBody({
  content,
  citations,
  onCitationClick,
}: {
  content: string;
  citations: Citation[];
  onCitationClick: (citation: Citation, answerText: string) => void;
}) {
  const parts = useMemo(() => parseContentWithCharts(content), [content]);
  return (
    <>
      {parts.map((p, i) => {
        if (p.type === 'chart') return <ChartRenderer key={i} chartData={p.chartData} />;
        if (!p.content.trim()) return null;
        return (
          <MessageWithInlineCitations
            key={i}
            content={p.content}
            citations={citations}
            onCitationClick={onCitationClick}
          />
        );
      })}
    </>
  );
}

function MessageWithInlineCitations({
  content,
  citations,
  onCitationClick,
}: {
  content: string;
  citations: Citation[];
  onCitationClick: (citation: Citation, answerText: string) => void;
}) {
  // Split content into paragraphs/blocks and assign citations
  const contentBlocks = useMemo(() => {
    // Split by double newlines (paragraphs) or bullet/numbered list items
    const blocks: { text: string; citations: Citation[] }[] = [];
    const lines = content.split('\n');
    let currentBlock = '';
    let blockIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isListItem = /^[\s]*[-*•]\s/.test(line) || /^[\s]*\d+\.\s/.test(line);
      const isHeading = /^#{1,6}\s/.test(line);
      const isEmpty = line.trim() === '';

      if (isEmpty && currentBlock.trim()) {
        // End of paragraph
        const relevant = findRelevantCitations(currentBlock, citations);
        blocks.push({
          text: currentBlock,
          citations: relevant.length > 0 ? relevant : (blockIndex < 3 ? citations.slice(0, 2) : [])
        });
        currentBlock = '';
        blockIndex++;
      } else if (isListItem || isHeading) {
        // Save previous block if exists
        if (currentBlock.trim()) {
          const relevant = findRelevantCitations(currentBlock, citations);
          blocks.push({
            text: currentBlock,
            citations: relevant.length > 0 ? relevant : (blockIndex < 3 ? citations.slice(0, 2) : [])
          });
          currentBlock = '';
          blockIndex++;
        }
        // List item or heading as its own block
        const relevant = findRelevantCitations(line, citations);
        blocks.push({
          text: line,
          citations: relevant.length > 0 ? relevant : []
        });
        blockIndex++;
      } else {
        currentBlock += (currentBlock ? '\n' : '') + line;
      }
    }

    // Add remaining content
    if (currentBlock.trim()) {
      const relevant = findRelevantCitations(currentBlock, citations);
      blocks.push({
        text: currentBlock,
        citations: relevant.length > 0 ? relevant : (blockIndex < 3 ? citations.slice(0, 2) : [])
      });
    }

    return blocks;
  }, [content, citations]);

  // Custom components for ReactMarkdown to inject citations
  const components: Components = useMemo(() => ({
    p: ({ children, ...props }) => {
      const text = extractTextFromChildren(children);
      const block = contentBlocks.find(b => b.text.includes(text.substring(0, 50)));
      const relevantCitations = block?.citations || [];

      return (
        <p {...props} className="my-1">
          {children}
          {relevantCitations.length > 0 && (
            <InlineCitation
              citations={relevantCitations}
              onCitationClick={(citation) => onCitationClick(citation, content)}
            />
          )}
        </p>
      );
    },
    li: ({ children, ...props }) => {
      const text = extractTextFromChildren(children);
      const block = contentBlocks.find(b => b.text.includes(text.substring(0, 30)));
      const relevantCitations = block?.citations || [];

      return (
        <li {...props} className="my-0.5">
          {children}
          {relevantCitations.length > 0 && (
            <InlineCitation
              citations={relevantCitations}
              onCitationClick={(citation) => onCitationClick(citation, content)}
            />
          )}
        </li>
      );
    },
    h1: ({ children, ...props }) => (
      <h1 {...props} className="my-2 text-lg font-bold">{children}</h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 {...props} className="my-2 text-base font-bold">{children}</h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 {...props} className="my-2 text-sm font-bold">{children}</h3>
    ),
    table: ({ children, ...props }) => (
      <div className="my-2 overflow-hidden w-full">
        <table {...props} className="text-xs w-full table-fixed border-collapse border border-zinc-300 dark:border-zinc-600 [&_td]:border [&_td]:border-zinc-300 dark:border-zinc-600 [&_td]:p-1.5 [&_td]:whitespace-normal [&_td]:break-words [&_td]:overflow-hidden [&_td]:align-top [&_th]:border [&_th]:border-zinc-300 dark:border-zinc-600 [&_th]:p-1.5 [&_th]:whitespace-normal [&_th]:break-words [&_th]:overflow-hidden [&_th]:bg-zinc-100 dark:[&_th]:bg-zinc-800 [&_th]:font-semibold">{children}</table>
      </div>
    ),
    pre: ({ children, ...props }) => (
      <pre {...props} className="my-2 bg-muted text-foreground overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full" style={{ scrollbarWidth: 'thin' }}>
        {children}
      </pre>
    ),
  }), [contentBlocks, onCitationClick]);

  return (
    <div className="text-sm prose prose-sm dark:prose-invert max-w-none prose-ul:my-1 prose-ol:my-1 prose-pre:my-2 prose-pre:bg-muted prose-pre:text-foreground prose-code:text-primary prose-code:bg-primary/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none overflow-x-auto">
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={components}>
        {convertTableBlocks(content)}
      </ReactMarkdown>
    </div>
  );
}

// Convert [TABLE]...[/TABLE] blocks to proper GFM markdown tables
function convertTableBlocks(text: string): string {
  return text.replace(/\[TABLE\]\s*([\s\S]*?)\s*\[\/TABLE\]/g, (_match, tableContent: string) => {
    const rows = tableContent.trim().split('\n').filter((row: string) => row.trim());
    if (rows.length === 0) return '';

    const mdRows = rows.map((row: string) => {
      const cells = row.split('|').map((cell: string) => cell.trim());
      return '| ' + cells.join(' | ') + ' |';
    });

    // Add separator row after the first row (header)
    if (mdRows.length >= 1) {
      const headerCells = rows[0].split('|');
      const separator = '| ' + headerCells.map(() => '---').join(' | ') + ' |';
      mdRows.splice(1, 0, separator);
    }

    return '\n\n' + mdRows.join('\n') + '\n\n';
  });
}

// Helper to extract text content from React children
function extractTextFromChildren(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('');
  }
  if (children && typeof children === 'object' && 'props' in children) {
    return extractTextFromChildren((children as any).props?.children);
  }
  return '';
}

function ThinkingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <Bot className="h-4 w-4" />
      </div>
      <div className="bg-muted rounded-lg px-4 py-3">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm text-muted-foreground">
            Searching knowledge base...
          </span>
        </div>
      </div>
    </div>
  );
}

export function KBChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameSessionId, setRenameSessionId] = useState<string | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(null);
  const [selectedAnswerText, setSelectedAnswerText] = useState<string>('');
  const [citationDialogOpen, setCitationDialogOpen] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  // Scope picker state — replaces the old includeClientFiles toggle.
  //   kbEnabled        — search the global Knowledge Base
  //   clientMode       — 'none' (KB only) | 'all' (cross-client) | 'specific' (multi-select)
  //   selectedClientIds — when clientMode === 'specific'
  // Defaults: cast the widest net so the user gets something useful out of
  // the box — Knowledge Base on, all clients in scope, and both documents +
  // live accounting figures included.
  const [kbEnabled, setKbEnabled] = useState(true);
  const [clientMode, setClientMode] = useState<'none' | 'all' | 'specific'>('all');
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [scopeSearch, setScopeSearch] = useState('');
  const [dataSource, setDataSource] = useState<'documents' | 'accounting' | 'both'>('both');

  // Lazy-load the clients list when the scope picker opens
  const [scopeOpen, setScopeOpen] = useState(false);
  const { data: clientsData } = useQuery({
    queryKey: ['scope-clients'],
    queryFn: async () => {
      const resp = await apiClient.get('/clients?limit=500');
      const body = resp.data?.data || resp.data;
      const rows = body?.clients || body?.items || body || [];
      return (rows as any[]).map(c => ({
        id: c.id,
        name: c.displayName || c.display_name || c.name || 'Unknown',
        abn: c.abn,
      }));
    },
    enabled: scopeOpen || clientMode === 'specific',
    staleTime: 5 * 60 * 1000,
  });
  const allClients = clientsData || [];

  // Effective scope sent to backend
  const effectiveScope = useMemo<{ kb: boolean; clients: 'all' | 'none' | string[] }>(() => {
    if (clientMode === 'all') return { kb: kbEnabled, clients: 'all' };
    if (clientMode === 'specific' && selectedClientIds.length > 0)
      return { kb: kbEnabled, clients: selectedClientIds };
    return { kb: kbEnabled, clients: 'none' };
  }, [kbEnabled, clientMode, selectedClientIds]);

  // Human-readable summary of the current scope
  const scopeSummary = useMemo(() => {
    const parts: string[] = [];
    if (kbEnabled) parts.push('KB');
    if (clientMode === 'all') parts.push('all clients');
    else if (clientMode === 'specific' && selectedClientIds.length > 0) {
      const n = selectedClientIds.length;
      if (n === 1) {
        const cli = allClients.find(c => c.id === selectedClientIds[0]);
        parts.push(cli?.name || '1 client');
      } else parts.push(`${n} clients`);
      if (dataSource === 'accounting') parts.push('accounting');
      else if (dataSource === 'both') parts.push('docs+accounting');
    }
    return parts.join(' + ') || 'Pick a source';
  }, [kbEnabled, clientMode, selectedClientIds, dataSource, allClients]);

  const filteredScopeClients = useMemo(() => {
    if (!scopeSearch.trim()) return allClients.slice(0, 100);
    const q = scopeSearch.toLowerCase();
    return allClients
      .filter(c => c.name.toLowerCase().includes(q) || (c.abn || '').toLowerCase().includes(q))
      .slice(0, 100);
  }, [allClients, scopeSearch]);

  const toggleClient = (id: string) => {
    setSelectedClientIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id].slice(0, 25)
    );
    if (clientMode !== 'specific') setClientMode('specific');
  };
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const handleCitationClick = (citation: Citation, answerText?: string) => {
    setSelectedCitation(citation);
    setSelectedAnswerText(answerText || '');
    setCitationDialogOpen(true);
  };

  // Fetch chat sessions
  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['chat-sessions'],
    queryFn: () => chatSessionsApi.listSessions({ limit: 50 }),
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Poll for chat response when API Gateway times out (504)
  // The Lambda continues running and saves the result to DB
  const pollForResponse = async (
    pollSessionId: string,
    originalQuestion: string,
    afterTimestamp: string
  ) => {
    const maxAttempts = 90; // Poll for up to ~4.5 minutes (90 * 3s)
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((resolve) => setTimeout(resolve, 3000)); // Wait 3 seconds
      try {
        const statusRes = await apiClient.get<{
          status: string;
          message?: string;
          sessionId?: string;
          modelId?: string;
          responseTime?: number;
          citations?: Citation[];
          queryId?: string;
        }>(`/chat/status/${pollSessionId}`, {
          params: { after: afterTimestamp },
        });

        if ((statusRes.data.status === 'completed' || statusRes.data.status === 'error') && statusRes.data.message) {
          // Response is ready (or error occurred) - update messages with citations
          const isError = statusRes.data.status === 'error';
          const polledCitations = isError ? [] : (statusRes.data.citations || []);
          setMessages((prev) => {
            // Remove the "still thinking" placeholder
            const withoutPlaceholder = prev.filter((m) => m.id !== 'polling-placeholder');
            return [
              ...withoutPlaceholder,
              {
                id: crypto.randomUUID(),
                role: 'assistant' as const,
                content: statusRes.data.message!,
                citations: polledCitations,
                timestamp: new Date(),
                originalQuestion,
                queryId: statusRes.data.queryId,
                canExplainMore: polledCitations.length > 0,
                error: isError || undefined,
              },
            ];
          });
          queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
          return;
        }
      } catch {
        // Polling request failed - continue trying
      }
    }
    // Polling exhausted - show error
    setMessages((prev) => {
      const withoutPlaceholder = prev.filter((m) => m.id !== 'polling-placeholder');
      return [
        ...withoutPlaceholder,
        {
          id: crypto.randomUUID(),
          role: 'assistant' as const,
          content: 'The response is taking longer than expected. Please try again.',
          timestamp: new Date(),
          error: true,
        },
      ];
    });
  };

  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      // For new conversations, generate a sessionId so we can poll on timeout
      const effectiveSessionId = sessionId || undefined;
      const newSid = !effectiveSessionId ? crypto.randomUUID() : undefined;

      // If starting a new session, set it immediately so polling can use it
      if (newSid) {
        setSessionId(newSid);
      }

      const requestTimestamp = new Date().toISOString();
      try {
        const response = await apiClient.post<ChatResponse>('/chat', {
          message,
          sessionId: effectiveSessionId,
          newSessionId: newSid,
          scope: effectiveScope,
          dataSource,
        });
        return { data: response.data, originalQuestion: message };
      } catch (error: any) {
        // Check if this is a timeout (504) or network error
        const is504 = error.response?.status === 504;
        const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED';
        const isNetworkError = !error.response && error.message?.includes('timeout');

        if (is504 || isTimeout || isNetworkError) {
          const pollSid = effectiveSessionId || newSid;
          if (pollSid) {
            // Add "still thinking" placeholder
            setMessages((prev) => [
              ...prev,
              {
                id: 'polling-placeholder',
                role: 'assistant',
                content: 'Still thinking... The response is taking longer than usual. Please wait.',
                timestamp: new Date(),
              },
            ]);
            // Start polling in background
            pollForResponse(pollSid, message, requestTimestamp);
            // Return a sentinel so onSuccess knows not to add another message
            return { data: null, originalQuestion: message, polling: true };
          }
        }
        throw error;
      }
    },
    onSuccess: (result) => {
      if (!result || (result as any).polling) return; // Polling handles its own messages
      const { data, originalQuestion } = result as { data: ChatResponse; originalQuestion: string };
      if (!data) return;

      if (data.sessionId && data.sessionId !== sessionId) {
        setSessionId(data.sessionId);
        // Refresh sessions list to show new session
        queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      }

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.message,
        citations: data.citations,
        timestamp: new Date(),
        error: data.error,
        queryId: data.queryId,
        originalQuestion,
        canExplainMore: !data.error && data.citations && data.citations.length > 0,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Update usage counter in header
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
    },
    onError: (error: any) => {
      const isQuotaExceeded = error.response?.status === 429 || error.response?.data?.quotaExceeded;
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: isQuotaExceeded
          ? `${error.response?.data?.error || 'You have reached your AI query limit.'}\n\n[Upgrade your plan](/panel/upgrade) to continue using AI features.`
          : (error.response?.data?.message || 'An error occurred. Please try again.'),
        timestamp: new Date(),
        error: true,
      };

      setMessages((prev) => [...prev, errorMessage]);
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
    },
  });

  const explainMoreMutation = useMutation({
    mutationFn: async ({ originalQuestion, shortAnswer, citations }: { originalQuestion: string; shortAnswer: string; citations?: Citation[] }) => {
      const response = await apiClient.post<ChatResponse>('/chat', {
        explainMore: true,
        originalQuestion,
        shortAnswer,
        sessionId,
        // Pass existing citation content so backend skips redundant vector search
        existingContext: citations?.map(c => ({
          documentId: c.documentId,
          filename: c.filename,
          content: c.fullContent || c.excerpt,
          similarity: c.similarity,
          source: c.source,
        })),
      });
      return response.data;
    },
    onSuccess: (data) => {
      setMessages((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].role === 'assistant' && updated[i].canExplainMore) {
            updated[i] = { ...updated[i], canExplainMore: false };
            break;
          }
        }
        return [
          ...updated,
          {
            id: crypto.randomUUID(),
            role: 'assistant' as const,
            content: data.message,
            citations: data.citations,
            timestamp: new Date(),
            error: data.error,
            queryId: data.queryId,
            isExplanation: true,
          },
        ];
      });

      // Update usage counter in header
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
    },
    onError: () => {
      toast.error('Failed to get detailed explanation');
    },
  });

  const handleExplainMore = (originalQuestion: string, shortAnswer: string, citations?: Citation[]) => {
    explainMoreMutation.mutate({ originalQuestion, shortAnswer, citations });
  };

  const feedbackMutation = useMutation({
    mutationFn: async ({ queryId, rating }: { queryId: string; rating: FeedbackRating }) => {
      const response = await apiClient.post('/feedback', { queryId, rating });
      return response.data;
    },
    onSuccess: (_, variables) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.queryId === variables.queryId
            ? { ...msg, feedbackGiven: variables.rating }
            : msg
        )
      );
      toast.success('Thanks for your feedback!');
    },
    onError: () => {
      toast.error('Failed to submit feedback');
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: (id: string) => chatSessionsApi.deleteSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      if (sessionId === renameSessionId) {
        startNewChat();
      }
      toast.success('Conversation deleted');
    },
    onError: () => {
      toast.error('Failed to delete conversation');
    },
  });

  const renameSessionMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      chatSessionsApi.updateSession(id, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      setRenameDialogOpen(false);
      toast.success('Conversation renamed');
    },
    onError: () => {
      toast.error('Failed to rename conversation');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || chatMutation.isPending) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    chatMutation.mutate(input.trim());
    setInput('');
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(undefined);
    inputRef.current?.focus();
  };

  const loadSession = async (session: ChatSession) => {
    try {
      const data = await chatSessionsApi.getSession(session.id);
      setSessionId(session.id);
      setMessages(
        data.messages.map((msg: ApiChatMessage) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.createdAt),
        }))
      );
    } catch {
      toast.error('Failed to load conversation');
    }
  };

  const handleFeedback = (queryId: string, rating: FeedbackRating) => {
    feedbackMutation.mutate({ queryId, rating });
  };

  const openRenameDialog = (session: ChatSession) => {
    setRenameSessionId(session.id);
    setRenameTitle(session.title);
    setRenameDialogOpen(true);
  };

  const handleRename = () => {
    if (renameSessionId && renameTitle.trim()) {
      renameSessionMutation.mutate({ id: renameSessionId, title: renameTitle.trim() });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const FeedbackButtons = ({ message }: { message: ChatMessage }) => {
    if (!message.queryId || message.error) return null;

    const buttonClass = "h-7 w-7 p-0";
    const activeClass = "bg-primary/10";

    return (
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border/30">
        <span className="text-xs text-muted-foreground mr-2">Was this helpful?</span>
        <Button
          variant="ghost"
          size="sm"
          className={cn(buttonClass, message.feedbackGiven === 'helpful' && activeClass)}
          onClick={() => handleFeedback(message.queryId!, 'helpful')}
          disabled={!!message.feedbackGiven || feedbackMutation.isPending}
          title="Helpful"
        >
          <ThumbsUp className={cn("h-3.5 w-3.5", message.feedbackGiven === 'helpful' && "text-green-500")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(buttonClass, message.feedbackGiven === 'not_helpful' && activeClass)}
          onClick={() => handleFeedback(message.queryId!, 'not_helpful')}
          disabled={!!message.feedbackGiven || feedbackMutation.isPending}
          title="Not helpful"
        >
          <ThumbsDown className={cn("h-3.5 w-3.5", message.feedbackGiven === 'not_helpful' && "text-red-500")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(buttonClass, message.feedbackGiven === 'incorrect' && activeClass)}
          onClick={() => handleFeedback(message.queryId!, 'incorrect')}
          disabled={!!message.feedbackGiven || feedbackMutation.isPending}
          title="Incorrect information"
        >
          <MessageSquareWarning className={cn("h-3.5 w-3.5", message.feedbackGiven === 'incorrect' && "text-orange-500")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(buttonClass, message.feedbackGiven === 'incomplete' && activeClass)}
          onClick={() => handleFeedback(message.queryId!, 'incomplete')}
          disabled={!!message.feedbackGiven || feedbackMutation.isPending}
          title="Incomplete answer"
        >
          <FileQuestion className={cn("h-3.5 w-3.5", message.feedbackGiven === 'incomplete' && "text-yellow-500")} />
        </Button>
        {message.feedbackGiven && (
          <span className="text-xs text-muted-foreground ml-2">
            Feedback recorded
          </span>
        )}
      </div>
    );
  };

  const ExplainMoreButton = ({ message }: { message: ChatMessage }) => {
    if (!message.canExplainMore || message.error || !message.originalQuestion) return null;

    return (
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs text-primary hover:text-primary/80 hover:bg-primary/5 px-3 mt-3 rounded-full border-primary/30 hover:border-primary/50 transition-all"
        onClick={() => handleExplainMore(message.originalQuestion!, message.content, message.citations)}
        disabled={explainMoreMutation.isPending}
      >
        {explainMoreMutation.isPending ? (
          <>
            <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
            Loading...
          </>
        ) : (
          <>
            <ChevronDown className="h-3 w-3 mr-1.5" />
            Explain more
          </>
        )}
      </Button>
    );
  };

  const SourceCitation = ({ citation, answerText }: { citation: Citation; answerText: string }) => {
    const fullText = citation.fullContent || citation.excerpt;

    const getFileTypeInfo = (filename: string) => {
      const ext = filename.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return { label: 'PDF', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
      if (['doc', 'docx'].includes(ext || '')) return { label: 'Word', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
      if (['xls', 'xlsx'].includes(ext || '')) return { label: 'Excel', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
      if (['txt', 'md'].includes(ext || '')) return { label: 'Markdown', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };
      if (ext === 'html') return { label: 'HTML', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' };
      return { label: ext?.toUpperCase() || 'File', color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' };
    };

    const fileType = getFileTypeInfo(citation.filename);
    const relevancePercent = citation.similarity ? Math.round(citation.similarity * 100) : null;

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 hover:underline cursor-pointer transition-colors"
          >
            <FileText className="h-3 w-3" />
            <span className="truncate max-w-[150px]">{citation.filename}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[500px] max-w-[90vw] p-0"
          side="top"
          align="start"
        >
          <div className="flex items-center justify-between gap-2 p-3 border-b bg-muted/30">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-medium text-sm" title={citation.filename}>{truncateMiddle(citation.filename)}</span>
              <Badge variant="secondary" className={cn("text-[10px] px-1.5 py-0 shrink-0", fileType.color)}>
                {fileType.label}
              </Badge>
            </div>
            {citation.documentId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 shrink-0 text-xs gap-1"
                onClick={async () => {
                  try {
                    // Pick the right download endpoint based on citation source.
                    //   global       → global KB documents table
                    //   client_file  → tenant-synced files (from cross-client search)
                    //   other        → legacy /documents path (per-client uploads)
                    const endpoint =
                      citation.source === 'global'
                        ? `/global-knowledge/documents/${citation.documentId}/download`
                        : citation.source === 'client_file'
                          ? `/synced-files/${citation.documentId}/download`
                          : `/documents/${citation.documentId}/download`;
                    const res = await apiClient.get(endpoint);
                    const url = res.data?.data?.downloadUrl || res.data?.downloadUrl || res.data?.data?.url || res.data?.url;
                    if (url) window.open(url, '_blank');
                    else toast.error('No download URL returned');
                  } catch (err: any) {
                    toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to open file');
                  }
                }}
                title="Open full file"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <div
            className="max-h-[400px] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50"
            style={{ scrollbarWidth: 'thin' }}
          >
            <div className="p-4 overflow-hidden">
              <div className="prose prose-sm dark:prose-invert max-w-none break-words prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-headings:my-3 prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-pre:my-2 prose-pre:bg-muted prose-pre:text-foreground prose-code:text-primary prose-code:bg-primary/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-primary/50 prose-blockquote:bg-muted/30 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:not-italic prose-table:text-xs prose-th:bg-muted/50 prose-td:border-muted overflow-hidden">
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={{
                  table: ({ children, ...props }) => (
                    <div className="my-2 overflow-hidden w-full">
                      <table {...props} className="text-xs w-full table-fixed border-collapse border border-zinc-300 dark:border-zinc-600 [&_td]:border [&_td]:border-zinc-300 dark:border-zinc-600 [&_td]:p-1.5 [&_td]:whitespace-normal [&_td]:break-words [&_td]:overflow-hidden [&_td]:align-top [&_th]:border [&_th]:border-zinc-300 dark:border-zinc-600 [&_th]:p-1.5 [&_th]:whitespace-normal [&_th]:break-words [&_th]:overflow-hidden [&_th]:bg-zinc-100 dark:[&_th]:bg-zinc-800 [&_th]:font-semibold">{children}</table>
                    </div>
                  ),
                  pre: ({ children, ...props }) => (
                    <pre {...props} className="my-2 bg-muted text-foreground overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full" style={{ scrollbarWidth: 'thin' }}>
                      {children}
                    </pre>
                  ),
                }}>
                  {convertTableBlocks(fullText)}
                </ReactMarkdown>
              </div>
            </div>
          </div>

          {relevancePercent && (
            <div className="flex items-center gap-2 px-3 py-2 border-t bg-muted/30">
              <span className="text-xs text-muted-foreground">Relevance:</span>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all",
                    relevancePercent >= 70 ? "bg-green-500" :
                    relevancePercent >= 50 ? "bg-yellow-500" : "bg-orange-500"
                  )}
                  style={{ width: `${relevancePercent}%` }}
                />
              </div>
              <span className={cn(
                "text-xs font-medium",
                relevancePercent >= 70 ? "text-green-600 dark:text-green-400" :
                relevancePercent >= 50 ? "text-yellow-600 dark:text-yellow-400" : "text-orange-600 dark:text-orange-400"
              )}>
                {relevancePercent}%
              </span>
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4">
      {/* Chat History Sidebar */}
      <div
        className={cn(
          "flex flex-col border rounded-lg bg-card transition-all duration-300 overflow-hidden",
          sidebarOpen ? "w-72 min-w-[288px] max-w-[288px]" : "w-0 border-0"
        )}
      >
        {sidebarOpen && (
          <>
            <div className="p-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4" />
                <span className="font-medium text-sm">Chat History</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setSidebarOpen(false)}
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            </div>

            <div className="p-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2"
                onClick={startNewChat}
              >
                <Plus className="h-4 w-4" />
                New Chat
              </Button>
            </div>

            <ScrollArea className="flex-1 [&>div>div]:!block">
              <div className="p-2 space-y-1">
                {sessionsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : sessionsData?.sessions.length === 0 ? (
                  <div className="text-center py-4 text-sm text-muted-foreground">
                    No conversations yet
                  </div>
                ) : (
                  sessionsData?.sessions.map((session) => (
                    <div
                      key={session.id}
                      className={cn(
                        "group flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors",
                        sessionId === session.id && "bg-muted"
                      )}
                      onClick={() => loadSession(session)}
                    >
                      <MessageSquare className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                      <div className="flex-1 overflow-hidden min-w-0">
                        <p className="text-sm font-medium truncate">{session.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {formatDate(session.lastMessageAt || session.createdAt)} · {session.messageCount} msgs
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSessionMutation.mutate(session.id);
                        }}
                        title="Delete chat"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {!sidebarOpen && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setSidebarOpen(true)}
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
            )}
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <BookOpen className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Firm AI Chat</h1>
              <p className="text-sm text-muted-foreground">
                Ask anything across your Knowledge Base, client documents, and live Xero / QuickBooks data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Data-source pills. The three sources only apply when at least
                one client is in scope — when "None" is selected for clients,
                there's nothing for the pills to filter, so they're disabled
                with a tooltip explaining why. */}
            <div className="flex items-center gap-1 rounded-md border bg-background p-0.5">
              {([
                { v: 'documents', label: 'Documents' },
                { v: 'accounting', label: 'Accounting' },
                { v: 'both', label: 'Both' },
              ] as const).map(opt => {
                const disabled =
                  clientMode === 'none' ||
                  (clientMode === 'specific' && selectedClientIds.length === 0);
                const isActive = dataSource === opt.v;
                return (
                  <button
                    key={opt.v}
                    onClick={() => !disabled && setDataSource(opt.v)}
                    disabled={disabled}
                    title={
                      disabled
                        ? 'Select "All clients" or pick specific clients to enable this filter'
                        : opt.v === 'documents' ? 'Search indexed documents'
                        : opt.v === 'accounting' ? 'Search live Xero / QuickBooks figures'
                        : 'Search documents + live accounting figures'
                    }
                    className={cn(
                      'px-2.5 py-1 text-xs rounded font-medium transition-colors',
                      isActive ? 'bg-[#5048e5] text-white' : 'hover:bg-muted',
                      // When disabled, dim everything but keep the active
                      // pill visible so the user can see what would be
                      // applied once they pick clients.
                      disabled && 'cursor-not-allowed',
                      disabled && !isActive && 'opacity-40',
                      disabled && isActive && 'opacity-60'
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Scope picker — choose KB and/or one or more clients */}
            <Popover open={scopeOpen} onOpenChange={setScopeOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <FolderOpen className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">Searching: {scopeSummary}</span>
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[360px] p-0">
                <div className="p-3 border-b space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sources</p>
                  <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-[#5048e5]" />
                      <span className="text-sm font-medium">Knowledge Base</span>
                      <span className="text-[11px] text-muted-foreground">global firm + ATO + ASIC</span>
                    </div>
                    <Switch checked={kbEnabled} onCheckedChange={setKbEnabled} />
                  </label>
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Clients</p>
                  <div className="grid grid-cols-3 gap-1.5 text-xs">
                    {(['none', 'all', 'specific'] as const).map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setClientMode(opt)}
                        className={cn(
                          'rounded-md border px-2 py-1.5 capitalize transition-colors',
                          clientMode === opt
                            ? 'bg-[#5048e5] text-white border-[#5048e5]'
                            : 'bg-background hover:bg-muted/50'
                        )}
                      >
                        {opt === 'none' ? 'None' : opt === 'all' ? 'All clients' : 'Pick clients'}
                      </button>
                    ))}
                  </div>

                  {clientMode === 'specific' && (
                    <div className="space-y-1.5 pt-1">
                      <Input
                        placeholder="Search clients…"
                        value={scopeSearch}
                        onChange={e => setScopeSearch(e.target.value)}
                        className="h-8 text-xs"
                      />
                      {selectedClientIds.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {selectedClientIds.map(id => {
                            const c = allClients.find(x => x.id === id);
                            return (
                              <Badge key={id} variant="secondary" className="text-[10px] gap-1">
                                {c?.name || id.slice(0, 6)}
                                <X
                                  className="h-3 w-3 cursor-pointer"
                                  onClick={() => toggleClient(id)}
                                />
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                      <ScrollArea className="h-44 border rounded-md">
                        <div className="divide-y">
                          {filteredScopeClients.map(c => {
                            const checked = selectedClientIds.includes(c.id);
                            return (
                              <button
                                type="button"
                                key={c.id}
                                onClick={() => toggleClient(c.id)}
                                className={cn(
                                  'w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-muted/40',
                                  checked && 'bg-[#5048e5]/5'
                                )}
                              >
                                <div className={cn(
                                  'h-3.5 w-3.5 rounded border flex items-center justify-center shrink-0',
                                  checked ? 'bg-[#5048e5] border-[#5048e5]' : 'border-muted-foreground/40'
                                )}>
                                  {checked && <Check className="h-2.5 w-2.5 text-white" />}
                                </div>
                                <span className="flex-1 truncate">{c.name}</span>
                                {c.abn && <span className="text-[10px] text-muted-foreground">{c.abn}</span>}
                              </button>
                            );
                          })}
                          {filteredScopeClients.length === 0 && (
                            <p className="text-[11px] text-muted-foreground text-center py-3">
                              {scopeSearch ? 'No matching clients' : 'No clients yet'}
                            </p>
                          )}
                        </div>
                      </ScrollArea>
                      <p className="text-[10px] text-muted-foreground">
                        {selectedClientIds.length}/25 selected
                      </p>
                    </div>
                  )}
                </div>

                <div className="p-2 border-t flex justify-end">
                  <Button size="sm" onClick={() => setScopeOpen(false)}>Done</Button>
                </div>
              </PopoverContent>
            </Popover>

            {messages.length > 0 && (
              <Button variant="outline" size="sm" onClick={startNewChat}>
                <Plus className="h-4 w-4 mr-2" />
                New Chat
              </Button>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <Card className="flex-1 flex flex-col overflow-hidden">
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Knowledge Base Assistant
              {sessionId && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  Session Active
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 p-0 overflow-hidden">
            <ScrollArea ref={scrollAreaRef} className="h-full">
              <div className="p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[50vh] text-center">
                    <BookOpen className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <h3 className="text-lg font-medium mb-2">Start a Conversation</h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Ask questions about your uploaded documents. The AI will search through
                      your knowledge base and provide answers with citations.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Your conversation history is saved automatically.
                    </p>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-lg">
                      {[
                        'What are the key topics of...',
                        'Summarize main findings',
                        'What policies exist?',
                        'Find info about...',
                      ].map((suggestion) => (
                        <Button
                          key={suggestion}
                          variant="outline"
                          size="sm"
                          className="text-left justify-start h-auto py-2 px-3"
                          onClick={() => {
                            setInput(suggestion);
                            inputRef.current?.focus();
                          }}
                        >
                          {suggestion}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        'flex gap-3',
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      )}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Bot className="h-4 w-4" />
                        </div>
                      )}
                      <div
                        className={cn(
                          'max-w-[80%] rounded-lg px-4 py-3 group/msg relative overflow-hidden',
                          message.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : message.error
                            ? 'bg-destructive/10 border border-destructive/20'
                            : 'bg-muted'
                        )}
                      >
                        {message.role === 'assistant' && !message.error && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 h-7 w-7 p-0 opacity-0 group-hover/msg:opacity-100 transition-opacity"
                            onClick={async () => {
                              await navigator.clipboard.writeText(message.content);
                              setCopiedMessageId(message.id);
                              setTimeout(() => setCopiedMessageId(null), 2000);
                            }}
                            title="Copy answer"
                          >
                            {copiedMessageId === message.id ? (
                              <Check className="h-3.5 w-3.5 text-green-500" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        )}
                        {message.error && (
                          <div className="flex items-center gap-2 text-destructive mb-2">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-xs font-medium">Error</span>
                          </div>
                        )}
                        {message.role === 'user' ? (
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        ) : (
                          <ChatMessageBody
                            content={message.content}
                            citations={message.citations || []}
                            onCitationClick={handleCitationClick}
                          />
                        )}

                        {message.citations && message.citations.length > 0 && (() => {
                          const citedIds = getCitedCitationIds(message.content, message.citations);
                          const citedCitations = message.citations.filter(c => citedIds.has(c.id));
                          if (citedCitations.length === 0) return null;
                          return (
                          <div className="mt-3 pt-3 border-t border-border/50">
                            <p className="text-xs font-medium mb-2.5 text-muted-foreground flex items-center gap-1.5">
                              <BookOpen className="h-3 w-3" />
                              Sources ({citedCitations.length})
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {citedCitations.map((citation) => (
                                <button
                                  key={citation.id}
                                  onClick={() => handleCitationClick(citation, message.content)}
                                  className={cn(
                                    'border rounded-md px-2 py-0.5 transition-colors text-left',
                                    citation.source === 'client_file'
                                      ? 'bg-green-500/5 hover:bg-green-500/10 border-green-500/20'
                                      : 'bg-primary/5 hover:bg-primary/10 border-primary/20'
                                  )}
                                >
                                  <span className={cn(
                                    'inline-flex items-center gap-1.5 text-xs font-medium',
                                    citation.source === 'client_file'
                                      ? 'text-green-700 hover:text-green-600'
                                      : 'text-primary hover:text-primary/80'
                                  )}>
                                    {citation.source === 'client_file' ? (
                                      <FolderOpen className="h-3 w-3" />
                                    ) : (
                                      <FileText className="h-3 w-3" />
                                    )}
                                    <span className="truncate max-w-[150px]">{citation.filename}</span>
                                    {citation.source === 'client_file' && citation.clientName && (
                                      <span className="text-[10px] bg-green-500/10 text-green-700 px-1 rounded">{citation.clientName}</span>
                                    )}
                                    {citation.source !== 'client_file' && (
                                      <span className="text-[10px] bg-primary/10 px-1 rounded">KB</span>
                                    )}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                          );
                        })()}

                        {message.role === 'assistant' && <ExplainMoreButton message={message} />}
                        {message.role === 'assistant' && <FeedbackButtons message={message} />}
                      </div>
                      {message.role === 'user' && (
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                          <User className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                  ))
                )}

                {(chatMutation.isPending || explainMoreMutation.isPending) && (
                  <ThinkingIndicator />
                )}
              </div>
            </ScrollArea>
          </CardContent>

          <div className="border-t p-4">
            <form onSubmit={handleSubmit} className="flex gap-2 items-end">
              <Textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (input.trim() && !chatMutation.isPending) {
                      handleSubmit(e as unknown as React.FormEvent);
                    }
                  }
                }}
                placeholder="Ask a question about your documents... (Shift+Enter for new line)"
                disabled={chatMutation.isPending}
                className="flex-1 min-h-[80px] max-h-[200px] resize-none"
                rows={3}
              />
              <Button type="submit" disabled={!input.trim() || chatMutation.isPending} className="h-10">
                {chatMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Searching: <span className="font-medium">{scopeSummary}</span>
            </p>
          </div>
        </Card>
      </div>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Conversation</DialogTitle>
          </DialogHeader>
          <Input
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            placeholder="Enter new title..."
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={!renameTitle.trim()}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Citation Detail Dialog */}
      <CitationDetailDialog
        citation={selectedCitation}
        open={citationDialogOpen}
        onOpenChange={setCitationDialogOpen}
        answerText={selectedAnswerText}
      />
    </div>
  );
}

export default KBChatPage;
